import { useMemo, type ReactNode } from 'react';
import {
  TenantProvider as ButterTenantProvider,
  createThemeCache,
} from '@butter/ui/providers';
import { useTenantHandle } from '~/services/appRuntime';
import { useClubQuery } from '~/services/internalApi/club';

/**
 * Supplies the tenant identity and branding this app has already resolved to the
 * shared `@butter/ui` provider.
 *
 * The two app-specific pieces:
 *
 * - **Handle** — `?tenant-handle=` from the URL, via `appRuntime`. Available on the
 *   FIRST render: `initializeAppRuntime()` parses the query string before React
 *   mounts, and `MissingParamsGuard` hard-errors without it, so every reachable
 *   route has one. It drives both the palette cache key and `TenantBrand` in
 *   `MainHeader`.
 * - **Palette** — the tenant embedded in the club payload. `useClubQuery` is
 *   already in flight on every route (`ClubDataPrefetcher` fires it on load), so
 *   this adds no request. It also means the palette and the first themed pixels
 *   arrive together: `ClubGate` holds a neutral skeleton until that same query
 *   resolves.
 *
 * The cache covers the routes `ClubGate` does not. `account-setup` and
 * `reset-password` render outside `MainLayout`, and `MainHeader` renders outside
 * `ClubGate` on every route — all of that paints before club data lands, so a
 * returning visitor sees their club's brand immediately instead of a purple frame.
 * It is keyed by handle, which is what makes this safe on an origin that serves
 * every tenant.
 *
 * **That benefit does not reach the embedded widget, and shouldn't be "fixed".**
 * Admin hands clubs a cross-origin `<iframe>` snippet (Settings → Website Links),
 * where Safari and Firefox block third-party `localStorage` — the reason this app
 * has `~/services/browserAdapter` at all. `createThemeCache` writes `localStorage`
 * directly, so there it no-ops and every cold start flashes the `:root` purple.
 * Routing it through `browserAdapter` would not help: the parent-bridge adapter
 * needs a bridge script the embed snippet doesn't install, so it falls through to
 * memory, and its async `get()` would move the read from the shared provider's
 * pre-paint phase 1a to post-paint phase 1b — losing the flash-free property in the
 * standalone case to buy nothing in the embedded one.
 */
export function TenantProvider({ children }: { children: ReactNode }) {
  const [tenantHandle] = useTenantHandle();
  const { data: club } = useClubQuery();

  // Memoized on the handle: a new cache object every render would give the
  // provider's persist callback a new identity and make it write on every render.
  // No handle → no cache, rather than an unscoped one.
  const cache = useMemo(
    () => (tenantHandle ? createThemeCache(tenantHandle) : undefined),
    [tenantHandle],
  );

  return (
    <ButterTenantProvider
      handle={tenantHandle}
      colorTheme={club?.tenant?.colorTheme}
      cache={cache}
    >
      {children}
    </ButterTenantProvider>
  );
}
